import { TransactionRequest, NetworkConfig } from '../types/wallet';

declare global {
  interface Window {
    ethereum?: any;
  }
}

export class MetaMaskService {
  private ethereum: any;

  constructor() {
    console.log('MetaMaskService: Initializing...');

    if (typeof window === 'undefined') {
      throw new Error('MetaMask can only be used in browser environment.');
    }

    this.ethereum = window.ethereum;

    if (!this.ethereum) {
      throw new Error('MetaMask not found. Please install MetaMask extension.');
    }

    console.log('MetaMaskService: Initialized successfully');
  }

  async connect(): Promise<string[]> {
    console.log('MetaMask: Starting connection...');

    try {
      // First check if already connected
      const existingAccounts = await this.ethereum.request({
        method: 'eth_accounts',
      });

      if (existingAccounts && existingAccounts.length > 0) {
        console.log('MetaMask: Using existing account:', existingAccounts[0]);
        return existingAccounts;
      }

      // Request new connection
      const accounts = await this.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please create an account in MetaMask.');
      }

      console.log('MetaMask: Connected successfully:', accounts[0]);
      return accounts;
    } catch (error: any) {
      console.error('MetaMask connection error:', error);

      if (error.code === 4001) {
        throw new Error('Connection rejected. Please approve the connection in MetaMask.');
      }
      if (error.code === -32002) {
        throw new Error('Connection request pending. Please check MetaMask popup.');
      }
      throw new Error(error.message || 'Failed to connect to MetaMask');
    }
  }

  async disconnect(): Promise<void> {
    // MetaMask doesn't have a programmatic disconnect method
    // The user needs to disconnect from the MetaMask extension
    return Promise.resolve();
  }

  async switchNetwork(chainId: string): Promise<void> {
    try {
      await this.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
    } catch (error: any) {
      if (error.code === 4902) {
        throw new Error('Network not added to MetaMask. Please add it manually.');
      }
      throw new Error(`Failed to switch network: ${error.message}`);
    }
  }

  async addNetwork(network: NetworkConfig): Promise<void> {
    try {
      await this.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${network.chainId.toString(16)}`,
            chainName: network.name,
            nativeCurrency: {
              name: network.symbol,
              symbol: network.symbol,
              decimals: network.decimals,
            },
            rpcUrls: [network.rpcUrl],
            blockExplorerUrls: [network.blockExplorerUrl],
          },
        ],
      });
    } catch (error: any) {
      throw new Error(`Failed to add network: ${error.message}`);
    }
  }

  async signMessage(message: string): Promise<string> {
    try {
      const accounts = await this.ethereum.request({
        method: 'eth_accounts',
      });
      
      if (accounts.length === 0) {
        throw new Error('No accounts connected');
      }

      const signature = await this.ethereum.request({
        method: 'personal_sign',
        params: [message, accounts[0]],
      });

      return signature;
    } catch (error: any) {
      throw new Error(`Failed to sign message: ${error.message}`);
    }
  }

  async sendTransaction(tx: TransactionRequest): Promise<string> {
    try {
      const accounts = await this.ethereum.request({
        method: 'eth_accounts',
      });
      
      if (accounts.length === 0) {
        throw new Error('No accounts connected');
      }

      const transactionParameters = {
        from: accounts[0],
        to: tx.to,
        value: tx.value || '0x0',
        data: tx.data || '0x',
        gasLimit: tx.gasLimit,
        gasPrice: tx.gasPrice,
      };

      const txHash = await this.ethereum.request({
        method: 'eth_sendTransaction',
        params: [transactionParameters],
      });

      return txHash;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new Error('User rejected the transaction.');
      }
      throw new Error(`Transaction failed: ${error.message}`);
    }
  }

  async getBalance(address: string): Promise<string> {
    try {
      const balance = await this.ethereum.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      return balance;
    } catch (error: any) {
      throw new Error(`Failed to get balance: ${error.message}`);
    }
  }

  isConnected(): boolean {
    return this.ethereum && this.ethereum.isConnected();
  }

  async getChainId(): Promise<string> {
    try {
      const chainId = await this.ethereum.request({
        method: 'eth_chainId',
      });
      return chainId;
    } catch (error: any) {
      throw new Error(`Failed to get chain ID: ${error.message}`);
    }
  }

  // Event listeners for account and network changes
  onAccountsChanged(callback: (accounts: string[]) => void): void {
    if (this.ethereum) {
      this.ethereum.on('accountsChanged', callback);
    }
  }

  onChainChanged(callback: (chainId: string) => void): void {
    if (this.ethereum) {
      this.ethereum.on('chainChanged', callback);
    }
  }

  removeAllListeners(): void {
    if (this.ethereum) {
      this.ethereum.removeAllListeners();
    }
  }
}